import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useEffect, useRef } from "react";
import { useMutation } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import classNames from "classnames";
import { DEVICE_SERIAL_NUMBER_QUERY_PARAM } from "@jti/fe-common";
import { BrowserStorage, CasAuthApi, User } from "@jti/rest-api";
import { Card, DiscreteHeaderBanner, Loading, TabItem, Tabs, useConfig, useTranslations, } from "@jti/ui";
import { DeviceCode } from "../../components/DeviceCode/DeviceCode";
import { useAemModel } from "../../contexts/AemModelContext";
import { useDeviceRegistrationModel } from "../../hooks/useDeviceRegistrationModel/useDeviceRegistrationModel";
import { useSearchParam } from "../../hooks/useSearchParam";
import { PhoneLogin } from "./PhoneLogin/PhoneLogin";
import EmailUpdate from "./EmailUpdate";
import LoginForm from "./LoginForm";
import NoAccount from "./NoAccount";
import styles from "./Login.module.scss";
const browserStorage = new BrowserStorage();
const LOGIN_FORM_MODE_CONSTANTS = {
    EMAIL: "email",
    PHONE: "phone",
    EMAIL_AND_PHONE: "email_and_phone",
};
const Login = () => {
    var _a, _b;
    const isEmailVerified = useRef(false);
    const { FAQ_PAGE } = useConfig();
    const { __ } = useTranslations();
    const { commonLoginSettings } = useAemModel();
    const { isDeviceRegistrationByLeafletEnabled } = useDeviceRegistrationModel();
    const deviceSerialFromUrl = useSearchParam(DEVICE_SERIAL_NUMBER_QUERY_PARAM);
    const loginMode = (_a = commonLoginSettings === null || commonLoginSettings === void 0 ? void 0 : commonLoginSettings.loginFormMode) !== null && _a !== void 0 ? _a : LOGIN_FORM_MODE_CONSTANTS.EMAIL;
    const loginModeDefaultTab = (_b = commonLoginSettings === null || commonLoginSettings === void 0 ? void 0 : commonLoginSettings.loginFormModeDefaultTab) !== null && _b !== void 0 ? _b : "email";
    const defaultTabNumber = loginModeDefaultTab === "phone" ? 1 : 0;
    const { token } = useParams();
    const history = useHistory();
    useEffect(() => {
        window.onpopstate = () => {
            var _a;
            if ((_a = history.location) === null || _a === void 0 ? void 0 : _a.state) {
                history.push({
                    pathname: "/reset-password",
                });
            }
        };
    }, [history]);
    const { isLoading, mutate, data, error } = useMutation((token) => CasAuthApi.init().verifyEmailUpdate(token), {
        onSuccess: () => {
            browserStorage.delete("hasPendingEmailVerification");
            const user = new User(new BrowserStorage());
            user.clearUser();
        },
    });
    useEffect(() => {
        if (!isEmailVerified.current && token) {
            isEmailVerified.current = true;
            mutate(token);
        }
    }, [isEmailVerified, token, mutate]);
    if (isLoading) {
        return _jsx(Loading, {});
    }
    if (token && (error === null || error === void 0 ? void 0 : error.statusCode)) {
        return _jsx(EmailUpdate, { errorStatusCode: error.statusCode });
    }
    const discreteHeaderBannerProps = data
        ? {
            description: __("login.update_email.success.body"),
            title: __("login.update_email.success.title"),
        }
        : {
            title: __("auth.page_title.title"),
            description: __("auth.page_title.sub_title"),
            linkLabel: __("auth.page_title.learn_more"),
            linkHref: FAQ_PAGE,
        };
    return (_jsxs(_Fragment, { children: [_jsx(DiscreteHeaderBanner, Object.assign({ dataTestId: "pageTitle" }, discreteHeaderBannerProps)), deviceSerialFromUrl && isDeviceRegistrationByLeafletEnabled && (_jsx("div", { className: styles.container, children: _jsx(DeviceCode, { code: deviceSerialFromUrl }) })), _jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.item, children: [loginMode === LOGIN_FORM_MODE_CONSTANTS.EMAIL && _jsx(LoginForm, {}), loginMode === LOGIN_FORM_MODE_CONSTANTS.PHONE && _jsx(PhoneLogin, {}), loginMode === LOGIN_FORM_MODE_CONSTANTS.EMAIL_AND_PHONE && (_jsxs(Tabs, { defaultTabIndex: defaultTabNumber, children: [_jsx(TabItem, { label: __("auth.login.email_tab_label"), children: _jsx(LoginForm, {}) }), _jsx(TabItem, { label: __("auth.login.phone_tab_label"), children: _jsx(Card, { children: _jsx(PhoneLogin, {}) }) })] }))] }), _jsx("div", { className: classNames(styles.item, {
                            [styles.loginModeNoAccount]: loginMode === LOGIN_FORM_MODE_CONSTANTS.EMAIL_AND_PHONE,
                        }), children: _jsx(NoAccount, {}) })] })] }));
};
export default memo(Login);
